import { useCallback, useState, useEffect, useRef } from "react";
import { Formik, Field, Form } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { isEmpty } from "lodash";
import * as Yup from "yup";
import moment from "moment";
import classNames from "classnames";
import "react-datepicker/dist/react-datepicker.css";
import Drawer, { selectSelectedDrawer } from "../../../organisms/Drawer";
import {
  BUTTON_TEXT,
  EMPTY_STRING,
  REGEX,
  BOOLEAN,
  VALIDATION_MESSAGES,
  EMPTY_OBJECT,
  QUESTION_MARK,
} from "../../../../constants";
import { CustomDatePicker } from "../../../molecules";
import { actions as drawerActions } from "../../../organisms/Drawer/drawers.reducers";
import { BACKGROUND_TASKS_FIELDS } from "../drawers.constants";
import {
  createBackgroundTask,
  selectSelectedDataModelId,
  selectSelectedBackgroundTask,
  updateBackgroundTask,
  selectServices,
  fetchServices,
  fetchBackgroundTasks,
  selectNewService,
} from "../../../../pages/DataModels";
import { actions as dataModelsActions } from "../../../../pages/DataModels/dataModels.reducers";
import Spinner, { SPINNER_NAMES } from "../../Spinner";
import { setSelectedModal } from "../../Modal";
import { MODALS } from "../../AppModals/modals.constants";
import CronExpressionModal from "../../AppModals/DataModelModals/CronExpressionModal";
import { renderExpression } from "../../../../helpers";

const { SUBMIT } = BUTTON_TEXT;
const { TRUE, FALSE } = BOOLEAN;
const { END_DATE } = BACKGROUND_TASKS_FIELDS;
const { MANAGE_SERVICE_MODAL } = MODALS;

const START_DATE_ADDED_DAYS = 0;
const DAYS = "days";
const FREQUENCY = "frequency";

const {
  SPECIAL_CHARACTERS,
  SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
  SPECIAL_CHARACTERS_WHITESPACES_AT_END,
} = REGEX;
const { setSelectedDrawer } = drawerActions;
const { setSelectedBackgroundTask, setBackgroundTaskFilters } =
  dataModelsActions;
const { CREATE_BACKGROUND_TASK, UPDATE_BACKGROUND_TASK } = SPINNER_NAMES;
const {
  BACKGROUND_TASK_NAME_IS_REQUIRED,
  NAME_SHOULD_CONTAIN,
  NAME_SHOULD_NOT_CONTAIN,
  MIN_CHAR_LENGTH,
  MAX_CHAR_LENGTH_TWENTY_ONE,
  START_DATE_IS__REQUIRED,
  NAME_SHOULD_NOT_CONTAIN_AT_END,
  SERVICE_IS_REQUIRED,
  FREQUENCY_IS__REQUIRED,
} = VALIDATION_MESSAGES;

const ManageBackgroundTaskDrawer = () => {
  const dispatch = useDispatch();
  const setFormikField = useRef({});

  const selectedDrawer = useSelector(selectSelectedDrawer);
  const dataModelId = useSelector(selectSelectedDataModelId);
  const selectedBackgroundTask = useSelector(selectSelectedBackgroundTask);
  const allServices = useSelector(selectServices);
  const newService = useSelector(selectNewService);

  const [defaultService, setDefaultService] = useState();
  const [initialValues, setInitialValues] = useState({
    name: EMPTY_STRING,
    frequency: EMPTY_OBJECT,
    startDate: moment().add(START_DATE_ADDED_DAYS, DAYS),
    shouldRepeat: FALSE,
    serviceId: EMPTY_STRING,
  });
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [showCronExpressionModal, setShowCronExpressionModal] = useState(false);
  const [expression, setExpression] = useState(EMPTY_STRING);

  useEffect(() => {
    const { setServiceField } = setFormikField.current;
    if (defaultService && setServiceField) setServiceField(defaultService.id);
  }, [defaultService]);

  useEffect(() => {
    const recentlyCreatedService = allServices.find(
      ({ name }) =>
        name === newService?.name &&
        newService?.serviceType === "backgroundTask"
    );
    setDefaultService(recentlyCreatedService);
  }, [allServices, newService]);

  useEffect(() => {
    if (selectedBackgroundTask) {
      const selectedFrequency = JSON.parse(selectedBackgroundTask.frequency);
      setInitialValues({
        name: selectedBackgroundTask.name,
        frequency: selectedFrequency,
        startDate: selectedBackgroundTask.startDate,
        shouldRepeat: selectedBackgroundTask.shouldRepeat ? TRUE : FALSE,
        serviceId: selectedBackgroundTask.serviceId ?? EMPTY_STRING,
      });
      if (
        selectedFrequency &&
        !Object.values(selectedFrequency).includes(EMPTY_STRING)
      ) {
        const { day, hour, minute, month, week, year } = selectedFrequency;
        setExpression(`${minute} ${hour} ${day} ${month} ${week} ${year}`);
      }
    }
  }, [selectedBackgroundTask]);

  const handleClose = useCallback(() => {
    dispatch(setSelectedDrawer(null));
    dispatch(setSelectedBackgroundTask(null));
  }, [dispatch]);

  const validationSchema = Yup.object({
    name: Yup.string()
      .required(BACKGROUND_TASK_NAME_IS_REQUIRED)
      .matches(
        SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
        NAME_SHOULD_CONTAIN
      )
      .matches(SPECIAL_CHARACTERS, NAME_SHOULD_NOT_CONTAIN)
      .min(3, MIN_CHAR_LENGTH)
      .max(21, MAX_CHAR_LENGTH_TWENTY_ONE)
      .matches(
        SPECIAL_CHARACTERS_WHITESPACES_AT_END,
        NAME_SHOULD_NOT_CONTAIN_AT_END
      ),
    startDate: Yup.string().required(START_DATE_IS__REQUIRED),
    serviceId: Yup.string().required(SERVICE_IS_REQUIRED),
    frequency: Yup.mixed().when("shouldRepeat", {
      is: TRUE,
      then: Yup.mixed().test(
        "check-frequency",
        FREQUENCY_IS__REQUIRED,
        function (value) {
          if (!value || isEmpty(value))
            return this.createError({
              path: FREQUENCY,
              message: FREQUENCY_IS__REQUIRED,
            });

          return true;
        }
      ),
      otherwise: Yup.mixed(),
    }),
  });

  const shouldSendFrequency = (shouldRepeat, frequency) => {
    return shouldRepeat === TRUE && frequency;
  };

  const handleDateSelection = useCallback((props = {}) => {
    const { field, actualDate, startDate, date, setFieldValue } = props;
    if (field === END_DATE && actualDate < startDate) {
      setFieldValue(`${field}`, new Date(startDate));
      return startDate;
    }
    if (actualDate) return new Date(actualDate);
    else {
      const currentDate = date ?? new Date();
      const selectedDate = date
        ? currentDate
        : currentDate.setDate(currentDate.getDate() + 1);
      if (field && setFieldValue)
        setFieldValue(`${field}`, new Date(selectedDate));

      return selectedDate;
    }
  }, []);

  const handleOnSubmit = useCallback(
    (values) => {
      const { name, shouldRepeat, startDate, frequency, serviceId } = values;

      const selectedFrequency =
        (shouldSendFrequency(shouldRepeat, frequency) && frequency) || null;

      const backgroundTaskDetails = {
        name,
        startDate,
        shouldRepeat: shouldRepeat === TRUE,
        frequency: JSON.stringify(selectedFrequency),
        dataModelId,
        serviceId,
      };

      dispatch(setBackgroundTaskFilters({ name: EMPTY_STRING }));
      dispatch(
        selectedBackgroundTask
          ? updateBackgroundTask({
              id: selectedBackgroundTask.id,
              backgroundTaskDetails,
            })
          : createBackgroundTask(backgroundTaskDetails)
      ).then((res) => {
        if (res.payload) {
          dispatch(fetchBackgroundTasks({ id: dataModelId }));
          dispatch(fetchServices({ id: dataModelId }));
          handleClose();
        }
      });
    },
    [dataModelId, dispatch, selectedBackgroundTask, handleClose]
  );

  const checkShouldShowFrequencySection = (shouldRepeat) =>
    shouldRepeat === TRUE;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleOnSubmit}
      validateOnMount={true}
    >
      {({
        values,
        errors,
        touched,
        handleReset,
        handleChange,
        handleBlur,
        setFieldValue,
        isValid,
        dirty,
        setFieldTouched,
      }) => {
        const dayOrWeekInputValid =
          values.frequency?.day === QUESTION_MARK ||
          values.frequency?.week === QUESTION_MARK;

        const cronExpression = expression
          ? `Cron Expression - (${expression})`
          : `Cron Expression`;

        const footer = (
          <div className="p-6 border-t border-gray-700 flex align-center justify-end">
            <button
              className="btn btn-primary-light mr-1"
              type="button"
              onClick={() => {
                handleReset();
                handleClose();
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={isSpinnerActive || !isValid || !dirty}
            >
              <Spinner
                name={
                  selectedBackgroundTask
                    ? UPDATE_BACKGROUND_TASK
                    : CREATE_BACKGROUND_TASK
                }
                setIsSpinnerActive={setIsSpinnerActive}
                persistSize
              >
                {SUBMIT}
              </Spinner>
            </button>
          </div>
        );

        const handleCustomizeCronExpression = () => {
          setFieldTouched(FREQUENCY, true);
          setShowCronExpressionModal(true);
        };

        return (
          <Form>
            <Drawer
              position="right"
              heading="Manage Background Task"
              selectedDrawer={selectedDrawer}
              handleReset={handleReset}
              handleClose={handleClose}
              footer={footer}
            >
              <div className="scrollbar flex-1 overflow-auto">
                <div className="space-y-4 p-4 border-b border-gray-700">
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Background Task Name
                    </label>
                    <Field
                      name="name"
                      type="text"
                      className={classNames("form-control border-2", {
                        "border-red-600": touched.name && errors.name,
                      })}
                      placeholder="Background Task Name"
                      value={values.name}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      autoComplete="off"
                      disabled={selectedBackgroundTask}
                      autoFocus
                    />
                    {touched.name && errors.name && (
                      <div className="text-sm font-medium text-red-500">
                        {errors.name}
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-4 p-4 border-b border-gray-700">
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Start Date
                    </label>
                    <div className="flex justify-start items-center relative">
                      <CustomDatePicker
                        name="startDate"
                        value={handleDateSelection({
                          actualDate: values.startDate,
                          setFieldValue,
                          field: "startDate",
                        })}
                        selected={handleDateSelection({
                          actualDate: values.startDate,
                        })}
                        onChange={(event) => setFieldValue("startDate", event)}
                        minDate={handleDateSelection()}
                      />
                    </div>
                    {touched.startDate && errors.startDate && (
                      <div className="text-sm font-medium text-red-500">
                        {errors.startDate}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <div
                      role="group"
                      className="flex flex-row"
                      aria-labelledby="my-radio-group"
                    >
                      <label className="text-sm font-medium text-gray-50 mb-1">
                        <Field
                          className="mr-2"
                          type="radio"
                          name="shouldRepeat"
                          value={FALSE}
                        />
                        Once
                      </label>
                      <label className="text-sm font-medium text-gray-50 mb-1 ml-32">
                        <Field
                          className="mr-2"
                          type="radio"
                          name="shouldRepeat"
                          value={TRUE}
                        />
                        Repeat
                      </label>
                    </div>
                  </div>
                  {checkShouldShowFrequencySection(values.shouldRepeat) && (
                    <>
                      <div className="form-group">
                        <label className="text-sm font-medium text-gray-50 mb-2 block">
                          {cronExpression}
                        </label>
                        <button
                          type="button"
                          className="btn bg-indigo-100 text-indigo-700 hover:bg-indigo-200 focus:bg-indigo-100"
                          onClick={handleCustomizeCronExpression}
                        >
                          Customize Cron Expression
                        </button>
                        {expression && (
                          <label className="text-sm font-normal italic text-gray-50 mt-2 block">
                            {renderExpression(
                              expression,
                              dayOrWeekInputValid,
                              Object.values(values.frequency).includes(
                                EMPTY_STRING
                              )
                            )}
                          </label>
                        )}
                        {touched.frequency && errors.frequency && (
                          <div className="text-sm font-medium text-red-500">
                            {errors.frequency}
                          </div>
                        )}
                        {showCronExpressionModal && (
                          <CronExpressionModal
                            cronExpression={expression}
                            setCronExpression={setExpression}
                            cronFrequency={values.frequency}
                            show={showCronExpressionModal}
                            onClose={() => setShowCronExpressionModal(false)}
                            setFieldValue={setFieldValue}
                          />
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="space-y-4 p-4 border-b border-gray-700">
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1">
                      Service
                    </label>
                    <Field
                      as="select"
                      name="serviceId"
                      className="form-control mb-1 cursor-pointer"
                      value={values.serviceId}
                      onChange={handleChange}
                    >
                      <option value={EMPTY_STRING} disabled>
                        Select Background Service
                      </option>
                      {allServices
                        .filter(
                          (service) => service.serviceType === "backgroundTask"
                        )
                        .map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name}
                          </option>
                        ))}
                    </Field>
                    <button
                      type="button"
                      className="btn btn-primary-light w-full"
                      onClick={() => {
                        dispatch(setSelectedModal(MANAGE_SERVICE_MODAL));
                        setFormikField.current = {
                          setServiceField: (value) =>
                            setFieldValue("serviceId", value),
                        };
                      }}
                    >
                      Add New Background Service
                    </button>
                  </div>
                </div>
              </div>
            </Drawer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ManageBackgroundTaskDrawer;
